import { useCallback, useRef, useState, useEffect } from 'react';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { getPointsToSmoothPath, detectPenInput } from '../utils/drawing';
import { getSmoothStrokePath, enhancedIOSSmoothing } from '../utils/strokeSmoothing';
import { createCatmullRomPath, gaussianSmooth, createCubicBezierPath } from '../utils/smoothPath';
import { Point, Tool } from '../types';
import { generateStrokeId } from '../utils/strokeUtils';
import { fabric, Path, Rect, Circle, Line, FabricObject, Point as FabricPoint } from '../lib/fabricLoader';
import type { Canvas as CanvasType } from 'fabric';
import { getObjectName } from '../utils/fabricHelpers';
import { findCircledOpticalChoice, findEliminatedOpticalChoice } from '../utils/circleDetection';
import { associateStrokeWithChoice, isStrokeOnOpticalChoice } from '../utils/opticalChoiceOverlay';

interface UseDrawingHandlersProps {
  fabricCanvasRef: React.MutableRefObject<CanvasType | null>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isDrawingModeEnabled: boolean;
  currentTool: string;
  currentColor: string;
  currentStrokeWidth: number;
  isMobile: boolean;
  isOpticalMode?: boolean;
  currentQuestionId?: string;
  onOpticalChoiceCircled?: (choiceIndex: number) => void;
  onOpticalChoiceEliminated?: (choiceIndex: number) => void;
}

// Define shape tools
export const SHAPE_TOOLS = [
  'rectangle', 'circle', 'line', 'triangle', 'arrow', 'star', 
  'hexagon', 'pentagon', 'ellipse', 'cube', 'cylinder', 'pyramid', 
  'coordinate', 'grid', 'parallelogram', 'trapezoid', 'diamond',
  'coordinateGrid' // Combined coordinate system with grid
] as const;

// Debug flag for palm rejection logging
const DEBUG_PALM_REJECTION = false;

// Interface for tracking pointer info
interface PointerInfo {
  type: string;
  timestamp: number;
  isPen: boolean;
  position: { x: number; y: number };
  isActivelyDrawing: boolean;
}

export const useDrawingHandlers = ({
  fabricCanvasRef,
  canvasRef,
  isDrawingModeEnabled,
  currentTool,
  currentColor,
  currentStrokeWidth,
  isMobile,
  isOpticalMode = false,
  currentQuestionId,
  onOpticalChoiceCircled,
  onOpticalChoiceEliminated
}: UseDrawingHandlersProps) => {
  const {
    strokes,
    addStroke,
    updateStroke,
    recordingSession,
    addRecordingEvent,
    isHoveringCanvas,
    setIsHoveringCanvas,
    removeStroke,
    isPenMode,
    touchEnabled,
    setPenMode
  } = useWhiteboardStore();
  
  const isRecording = recordingSession.isRecording;

  // Drawing state - use state for isDrawing to trigger re-renders
  const [isDrawing, setIsDrawingState] = useState(false);
  const isDrawingRef = useRef(false);
  const isPinchingRef = useRef(false);
  
  // Helper to update both state and ref
  const setIsDrawing = useCallback((value: boolean) => {
    setIsDrawingState(value);
    isDrawingRef.current = value;
  }, []);
  const currentPathRef = useRef<Point[]>([]);
  const currentStrokeIdRef = useRef<string | null>(null);
  const lastPointerTypeRef = useRef<string>('mouse');
  const drawingStartTimeRef = useRef<number>(0);
  const lastPointRef = useRef<Point | null>(null);
  const pointerCache = useRef<PointerEvent[]>([]);
  const initialPinchDistance = useRef<number>(0);
  
  // Palm rejection state
  const activePointers = useRef<Map<number, PointerInfo>>(new Map());
  const rejectedTouches = useRef<Set<number>>(new Set());
  const penCooldownRef = useRef<NodeJS.Timeout | null>(null);
  
  // iOS WebView specific state
  const lastValidPenEventRef = useRef<number>(0);
  const iosEventQueueRef = useRef<PointerEvent[]>([]);

  // Eraser state
  const areaEraserStartRef = useRef<Point | null>(null);
  const areaEraserPreviewRef = useRef<Rect | null>(null);
  
  // Palm rejection helper function
  const shouldAllowInput = useCallback((event: any): boolean => {
    // Extract pointer event from fabric event or use directly
    let pointerEvent: PointerEvent | null = null;
    
    if (event && 'pointerId' in event) {
      pointerEvent = event as PointerEvent;
    } else if (event && event.e && 'pointerId' in event.e) {
      pointerEvent = event.e as PointerEvent;
    }
    
    if (!pointerEvent) {
      // Not a pointer event, allow by default
      return true;
    }
    
    // Check if we're in iOS WebView
    const isIOSWebView = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
      (window.parent !== window || (window as any).webkit?.messageHandlers);
    
    const isPenInput = detectPenInput(pointerEvent);
    
    // iOS WebView specific handling
    if (isIOSWebView) {
      // Debug logging for iOS
      const DEBUG_IOS = localStorage.getItem('debug_ios_pen') === 'true';
      if (DEBUG_IOS) {
        console.log('[iOS Palm Rejection]', {
          pointerId: pointerEvent.pointerId,
          pointerType: pointerEvent.pointerType,
          isPenInput,
          isPenMode,
          touchEnabled,
          pressure: pointerEvent.pressure,
          radiusX: (pointerEvent as any).radiusX,
          radiusY: (pointerEvent as any).radiusY
        });
      }
      
      // Store pointer info for iOS
      const pointerInfo: PointerInfo = {
        type: pointerEvent.pointerType,
        timestamp: Date.now(),
        isPen: isPenInput,
        position: { x: pointerEvent.clientX, y: pointerEvent.clientY },
        isActivelyDrawing: false
      };
      
      // Track all pointers
      activePointers.current.set(pointerEvent.pointerId, pointerInfo);
      
      // iOS WebView palm rejection - more lenient
      if (isPenInput) {
        // Record last valid pen event time
        lastValidPenEventRef.current = Date.now();
        
        // If pen is detected, clear rejected touches to allow immediate pen input
        rejectedTouches.current.clear();
        
        // Clear iOS event queue when pen is detected
        iosEventQueueRef.current = [];
        
        // Enable pen mode
        if (!isPenMode) {
          setPenMode(true);
        }
        
        // Always allow pen input
        return true;
      }
      
      // For touch in iOS WebView, check if it might be a pen
      if (pointerEvent.pointerType === 'touch') {
        // If pen mode is active but touch is enabled, allow it
        if (isPenMode && touchEnabled) {
          return true;
        }
        
        // If pen mode is active and touch is disabled, check for pen-like characteristics
        if (isPenMode && !touchEnabled) {
          // Give iOS touches a chance if they have pen-like properties
          const hasValidPressure = pointerEvent.pressure > 0 && pointerEvent.pressure < 1;
          const hasSmallRadius = ('radiusX' in pointerEvent && (pointerEvent as any).radiusX < 5);
          
          // Also check if this might be a delayed pen event
          const timeSinceLastPen = Date.now() - lastValidPenEventRef.current;
          const mightBeDelayedPen = timeSinceLastPen < 100; // Within 100ms of last pen event
          
          if (hasValidPressure || hasSmallRadius || mightBeDelayedPen) {
            // Might be a pen, allow it
            if (DEBUG_IOS) {
              console.log('[iOS] Allowing touch that might be pen', {
                hasValidPressure,
                hasSmallRadius,
                mightBeDelayedPen,
                timeSinceLastPen
              });
            }
            return true;
          }
          
          // Otherwise reject the touch
          rejectedTouches.current.add(pointerEvent.pointerId);
          return false;
        }
      }
      
      return true;
    }
    
    // Non-iOS standard logic
    // Check if there's currently an active pen drawing
    const hasActivePen = Array.from(activePointers.current.values()).some(
      p => p.isPen && p.isActivelyDrawing
    );
    
    // Reject new touches if a pen is actively drawing
    if ((isPenMode && !touchEnabled) || hasActivePen) {
      if (pointerEvent.pointerType === 'touch') {
        rejectedTouches.current.add(pointerEvent.pointerId);
        return false;
      }
    }
    
    // If pen is detected, enable pen mode
    if (isPenInput && !isPenMode) {
      setPenMode(true);
    }
    
    return true;
  }, [isPenMode, touchEnabled, setPenMode]);

  // Convert points to path string for shapes (no smoothing)
  const getShapePathString = useCallback((points: Point[], tool?: string): string => {
    if (points.length < 2) return '';
    
    // Special handling for grid and coordinate - use pen up/down based on pressure
    if (tool === 'grid' || tool === 'coordinate' || tool === 'coordinateGrid') {
      return points.map((point, index) => {
        // Use M (move) for pen up (pressure 0) or first point
        if (index === 0 || point.pressure === 0) {
          return `M ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
        }
        return `L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
      }).join(' ');
    }
    
    // Use straight lines for other shapes
    return points.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
    ).join(' ');
  }, []);

  // Generate shape points based on tool type
  const generateShapePoints = useCallback((tool: string, startPoint: Point, endPoint: Point): Point[] => {
    const points: Point[] = [];
    const timestamp = Date.now(); // Use current timestamp for generated points
    
    switch (tool) {
      case 'rectangle':
        // Simple rectangle
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
        points.push({ x: endPoint.x, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ ...endPoint, timestamp: endPoint.timestamp || timestamp });
        points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp }); // Close
        break;
        
      case 'parallelogram':
        // Parallelogram with slanted sides
        const paraOffset = Math.abs(endPoint.x - startPoint.x) * 0.2;
        points.push({ x: startPoint.x + paraOffset, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x - paraOffset, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + paraOffset, y: startPoint.y, pressure: 0.5, timestamp }); // Close
        break;
        
      case 'trapezoid':
        // Trapezoid - narrower at top
        const trapOffset = Math.abs(endPoint.x - startPoint.x) * 0.15;
        points.push({ x: startPoint.x + trapOffset, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x - trapOffset, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + trapOffset, y: startPoint.y, pressure: 0.5, timestamp }); // Close
        break;
      
      case 'circle':
      case 'ellipse':
        // Generate circle/ellipse points
        const centerX = (startPoint.x + endPoint.x) / 2;
        const centerY = (startPoint.y + endPoint.y) / 2;
        const radiusX = Math.abs(endPoint.x - startPoint.x) / 2;
        const radiusY = Math.abs(endPoint.y - startPoint.y) / 2;
        const steps = 72; // More points for smoother circle/ellipse
        
        for (let i = 0; i <= steps; i++) {
          const angle = (i / steps) * Math.PI * 2;
          points.push({
            x: centerX + radiusX * Math.cos(angle),
            y: centerY + radiusY * Math.sin(angle),
            pressure: 0.5,
            timestamp
          });
        }
        break;
      
      case 'line':
      case 'arrow':
        // Simple line
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
        points.push({ ...endPoint, timestamp: endPoint.timestamp || timestamp });
        break;
      
      case 'triangle':
        const midX = (startPoint.x + endPoint.x) / 2;
        // Use the actual drag rectangle dimensions
        const width = Math.abs(endPoint.x - startPoint.x);
        const height = Math.abs(endPoint.y - startPoint.y);
        
        // If height is very small (horizontal drag), create an equilateral triangle
        const triangleHeight = height < 5 ? width * Math.sqrt(3) / 2 : height;
        
        // Determine the top and bottom based on drag direction
        let topY, bottomY;
        if (height < 5) {
          // Horizontal drag - create triangle pointing up from the line
          topY = Math.min(startPoint.y, endPoint.y) - triangleHeight;
          bottomY = Math.max(startPoint.y, endPoint.y);
        } else {
          // Normal drag - use actual positions
          topY = Math.min(startPoint.y, endPoint.y);
          bottomY = Math.max(startPoint.y, endPoint.y);
        }
        
        points.push({ x: midX, y: topY, pressure: 0.5, timestamp });
        points.push({ x: Math.min(startPoint.x, endPoint.x), y: bottomY, pressure: 0.5, timestamp });
        points.push({ x: Math.max(startPoint.x, endPoint.x), y: bottomY, pressure: 0.5, timestamp });
        points.push({ x: midX, y: topY, pressure: 0.5, timestamp });
        break;
      
      case 'star':
        // 5-pointed star
        const cx = (startPoint.x + endPoint.x) / 2;
        const cy = (startPoint.y + endPoint.y) / 2;
        const outerRadius = Math.min(Math.abs(endPoint.x - startPoint.x), Math.abs(endPoint.y - startPoint.y)) / 2;
        const innerRadius = outerRadius * 0.4;
        const numPoints = 5;
        
        for (let i = 0; i <= numPoints * 2; i++) {
          const angle = (i / (numPoints * 2)) * Math.PI * 2 - Math.PI / 2;
          const radius = i % 2 === 0 ? outerRadius : innerRadius;
          points.push({
            x: cx + radius * Math.cos(angle),
            y: cy + radius * Math.sin(angle),
            pressure: 0.5,
            timestamp
          });
        }
        break;
      
      case 'hexagon':
      case 'pentagon':
        const polygonCenter = { x: (startPoint.x + endPoint.x) / 2, y: (startPoint.y + endPoint.y) / 2 };
        const polygonRadius = Math.min(Math.abs(endPoint.x - startPoint.x), Math.abs(endPoint.y - startPoint.y)) / 2;
        const sides = tool === 'hexagon' ? 6 : 5;
        
        for (let i = 0; i <= sides; i++) {
          const angle = (i / sides) * Math.PI * 2 - Math.PI / 2;
          points.push({
            x: polygonCenter.x + polygonRadius * Math.cos(angle),
            y: polygonCenter.y + polygonRadius * Math.sin(angle),
            pressure: 0.5,
            timestamp
          });
        }
        break;
      
      case 'diamond':
        const diamondCx = (startPoint.x + endPoint.x) / 2;
        const diamondCy = (startPoint.y + endPoint.y) / 2;
        const diamondWidth = Math.abs(endPoint.x - startPoint.x) / 2;
        const diamondHeight = Math.abs(endPoint.y - startPoint.y) / 2;
        
        points.push({ x: diamondCx, y: startPoint.y, pressure: 0.5, timestamp }); // Top
        points.push({ x: endPoint.x, y: diamondCy, pressure: 0.5, timestamp }); // Right
        points.push({ x: diamondCx, y: endPoint.y, pressure: 0.5, timestamp }); // Bottom
        points.push({ x: startPoint.x, y: diamondCy, pressure: 0.5, timestamp }); // Left
        points.push({ x: diamondCx, y: startPoint.y, pressure: 0.5, timestamp }); // Close
        break;
      
      case 'cube':
        // 3D cube with isometric projection
        const cubeSize = Math.min(Math.abs(endPoint.x - startPoint.x), Math.abs(endPoint.y - startPoint.y));
        const offset = cubeSize * 0.3;
        
        // Front face
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
        points.push({ x: startPoint.x + cubeSize, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + cubeSize, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
        
        // Move to back top-left
        points.push({ x: startPoint.x + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
        
        // Back face edges
        points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + cubeSize, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + cubeSize, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y + cubeSize - offset, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
        
        // Connect remaining vertices
        points.push({ x: startPoint.x + offset, y: startPoint.y - offset, pressure: 0.5, timestamp });
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
        points.push({ x: startPoint.x, y: startPoint.y + cubeSize, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + offset, y: startPoint.y + cubeSize - offset, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + cubeSize + offset, y: startPoint.y + cubeSize - offset, pressure: 0.5, timestamp });
        break;
      
      case 'cylinder':
        // Cylinder with elliptical top and bottom
        const cylCenterX = (startPoint.x + endPoint.x) / 2;
        const cylRadiusX = Math.abs(endPoint.x - startPoint.x) / 2;
        const cylRadiusY = cylRadiusX * 0.25;
        const cylSegments = 24;
        
        // Top ellipse (complete)
        for (let i = 0; i <= cylSegments; i++) {
          const angle = (Math.PI * 2 * i) / cylSegments;
          points.push({
            x: cylCenterX + cylRadiusX * Math.cos(angle),
            y: startPoint.y + cylRadiusY * Math.sin(angle),
            pressure: 0.5,
            timestamp
          });
        }
        
        // Right side line
        points.push({ x: cylCenterX + cylRadiusX, y: endPoint.y, pressure: 0.5, timestamp });
        
        // Bottom ellipse (front half only)
        for (let i = 0; i <= cylSegments / 2; i++) {
          const angle = (Math.PI * i) / (cylSegments / 2);
          points.push({
            x: cylCenterX + cylRadiusX * Math.cos(angle),
            y: endPoint.y + cylRadiusY * Math.sin(angle),
            pressure: 0.5,
            timestamp
          });
        }
        
        // Left side line
        points.push({ x: cylCenterX - cylRadiusX, y: startPoint.y, pressure: 0.5, timestamp });
        break;
      
      case 'pyramid':
        // 3D pyramid
        const pyrBase = Math.abs(endPoint.x - startPoint.x);
        const pyrHeight = Math.abs(endPoint.y - startPoint.y);
        const pyrCenterX = (startPoint.x + endPoint.x) / 2;
        
        
        // Base (trapezoid for perspective)
        points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x - pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        
        // Apex
        points.push({ x: pyrCenterX, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ x: pyrCenterX, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x - pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
        points.push({ x: pyrCenterX, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: startPoint.x + pyrBase * 0.2, y: endPoint.y - pyrHeight * 0.2, pressure: 0.5, timestamp });
        
        break;
      
      case 'grid':
        // Grid pattern
        const gridColCount = 8;
        const gridRowCount = 8;
        const gridCellWidth = (endPoint.x - startPoint.x) / gridColCount;
        const gridCellHeight = (endPoint.y - startPoint.y) / gridRowCount;
        
        // Vertical lines
        for (let col = 0; col <= gridColCount; col++) {
          const x = startPoint.x + col * gridCellWidth;
          if (col > 0) {
            // Pen up (move without drawing)
            points.push({ x, y: startPoint.y, pressure: 0, timestamp });
          }
          points.push({ x, y: startPoint.y, pressure: 0.5, timestamp });
          points.push({ x, y: endPoint.y, pressure: 0.5, timestamp });
        }
        
        // Horizontal lines
        for (let row = 0; row <= gridRowCount; row++) {
          const y = startPoint.y + row * gridCellHeight;
          // Pen up
          points.push({ x: startPoint.x, y, pressure: 0, timestamp });
          points.push({ x: startPoint.x, y, pressure: 0.5, timestamp });
          points.push({ x: endPoint.x, y, pressure: 0.5, timestamp });
        }
        break;
      
      case 'coordinate':
        // Coordinate system - just axes without grid
        const coordCenterX = (startPoint.x + endPoint.x) / 2;
        const coordCenterY = (startPoint.y + endPoint.y) / 2;
        
        // X-axis
        points.push({ x: startPoint.x, y: coordCenterY, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: coordCenterY, pressure: 0.5, timestamp });
        
        // X-axis arrow (simple V shape)
        points.push({ x: endPoint.x - 10, y: coordCenterY - 5, pressure: 0, timestamp }); // Pen up
        points.push({ x: endPoint.x - 10, y: coordCenterY - 5, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: coordCenterY, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x - 10, y: coordCenterY + 5, pressure: 0.5, timestamp });
        
        // Y-axis
        points.push({ x: coordCenterX, y: startPoint.y, pressure: 0, timestamp }); // Pen up
        points.push({ x: coordCenterX, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: coordCenterX, y: endPoint.y, pressure: 0.5, timestamp });
        
        // Y-axis arrow (simple V shape)
        points.push({ x: coordCenterX - 5, y: startPoint.y + 10, pressure: 0, timestamp }); // Pen up
        points.push({ x: coordCenterX - 5, y: startPoint.y + 10, pressure: 0.5, timestamp });
        points.push({ x: coordCenterX, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: coordCenterX + 5, y: startPoint.y + 10, pressure: 0.5, timestamp });
        break;
      
      case 'coordinateGrid':
        // Combined coordinate system with grid background (like old whiteboard)
        const cgCenterX = (startPoint.x + endPoint.x) / 2;
        const cgCenterY = (startPoint.y + endPoint.y) / 2;
        const cgSpacing = 30;
        
        // First draw grid lines (light gray)
        // Vertical lines
        const startCol = Math.floor(startPoint.x / cgSpacing) * cgSpacing;
        const endCol = Math.ceil(endPoint.x / cgSpacing) * cgSpacing;
        
        for (let x = startCol; x <= endCol; x += cgSpacing) {
          if (x >= startPoint.x && x <= endPoint.x) {
            if (x !== startCol) points.push({ x, y: startPoint.y, pressure: 0, timestamp }); // Pen up
            // Mark grid lines with special pressure value for light rendering
            points.push({ x, y: startPoint.y, pressure: -0.2, timestamp }); // Negative pressure = grid line
            points.push({ x, y: endPoint.y, pressure: -0.2, timestamp });
          }
        }
        
        // Horizontal lines
        const startRow = Math.floor(startPoint.y / cgSpacing) * cgSpacing;
        const endRow = Math.ceil(endPoint.y / cgSpacing) * cgSpacing;
        
        for (let y = startRow; y <= endRow; y += cgSpacing) {
          if (y >= startPoint.y && y <= endPoint.y) {
            points.push({ x: startPoint.x, y, pressure: 0, timestamp }); // Pen up
            points.push({ x: startPoint.x, y, pressure: -0.2, timestamp }); // Grid line
            points.push({ x: endPoint.x, y, pressure: -0.2, timestamp });
          }
        }
        
        // Then draw axes (black and thick)
        // X-axis
        points.push({ x: startPoint.x, y: cgCenterY, pressure: 0, timestamp }); // Pen up
        points.push({ x: startPoint.x, y: cgCenterY, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: cgCenterY, pressure: 0.5, timestamp });
        
        // X-axis arrow
        points.push({ x: endPoint.x - 10, y: cgCenterY - 5, pressure: 0, timestamp }); // Pen up
        points.push({ x: endPoint.x - 10, y: cgCenterY - 5, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x, y: cgCenterY, pressure: 0.5, timestamp });
        points.push({ x: endPoint.x - 10, y: cgCenterY + 5, pressure: 0.5, timestamp });
        
        // Y-axis
        points.push({ x: cgCenterX, y: startPoint.y, pressure: 0, timestamp }); // Pen up
        points.push({ x: cgCenterX, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: cgCenterX, y: endPoint.y, pressure: 0.5, timestamp });
        
        // Y-axis arrow
        points.push({ x: cgCenterX - 5, y: startPoint.y + 10, pressure: 0, timestamp }); // Pen up
        points.push({ x: cgCenterX - 5, y: startPoint.y + 10, pressure: 0.5, timestamp });
        points.push({ x: cgCenterX, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ x: cgCenterX + 5, y: startPoint.y + 10, pressure: 0.5, timestamp });
        break;
      
      default:
        // Default to rectangle for unknown shapes
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
        points.push({ x: endPoint.x, y: startPoint.y, pressure: 0.5, timestamp });
        points.push({ ...endPoint, timestamp: endPoint.timestamp || timestamp });
        points.push({ x: startPoint.x, y: endPoint.y, pressure: 0.5, timestamp });
        points.push({ ...startPoint, timestamp: startPoint.timestamp || timestamp });
    }
    
    return points;
  }, []);

  // Get pointer position
  const getPointerPosition = useCallback((e: any): Point => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return { x: 0, y: 0, pressure: 0.5, timestamp: Date.now() };

    let point;
    let pressure = 0.5; // Default pressure
    let timestamp = Date.now(); // Current timestamp
    
    // For fabric events, use the pre-calculated pointer
    if (e.fabricPointer) {
      point = e.fabricPointer;
    } else if (e.clientX !== undefined && e.clientY !== undefined && !e.touches) {
      // For mouse events, use canvas.getPointer to get correct coordinates
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        point = canvas.getPointer(e);
      } else {
        point = { x: e.clientX, y: e.clientY };
      }
    } else if (e.touches && e.touches.length > 0) {
      // Touch event
      const touch = e.touches[0];
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        // Use canvas.getPointer for touch events too to account for viewport transform
        point = canvas.getPointer(touch as any);
      } else {
        point = { x: touch.clientX, y: touch.clientY };
      }
    } else if (e.e) {
      // Fabric.js event
      point = canvas.getPointer(e.e);
    } else {
      point = { x: 0, y: 0 };
    }
    
    // Extract pressure from pointer event if available
    if (e && 'pressure' in e) {
      pressure = e.pressure;
    } else if (e && e.e && 'pressure' in e.e) {
      pressure = e.e.pressure;
    }
    
    // Extract timestamp if available
    if (e && 'timeStamp' in e) {
      timestamp = e.timeStamp;
    } else if (e && e.e && 'timeStamp' in e.e) {
      timestamp = e.e.timeStamp;
    }

    // Don't clamp the coordinates - let fabric handle that
    return {
      x: point.x,
      y: point.y,
      pressure: pressure, // Use actual pressure when available
      timestamp: timestamp
    };
  }, [fabricCanvasRef, canvasRef]);

  // Throttle rendering for performance
  const lastRenderTime = useRef(0);
  const renderThrottle = 16; // 60 FPS
  
  // Helper function - updateCanvasPath moved here to avoid circular dependency
  const updateCanvasPath = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || currentPathRef.current.length < 2) return;
    
    // Throttle renders
    const now = Date.now();
    if (now - lastRenderTime.current < renderThrottle) {
      return;
    }
    lastRenderTime.current = now;

    // Update canvas path

    // Generate path data based on tool type
    let pathData: string;
    
    if (SHAPE_TOOLS.includes(currentTool as any) && currentPathRef.current.length === 2) {
      // For shapes, generate shape points and use straight lines
      const shapePoints = generateShapePoints(currentTool, currentPathRef.current[0], currentPathRef.current[1]);
      pathData = getShapePathString(shapePoints, currentTool);
      
    } else {
      // For freehand drawing, use advanced smoothing
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (currentTool === 'pen' || currentTool === 'pencil') {
        // Apply gaussian smoothing first
        const smoothedPoints = gaussianSmooth(currentPathRef.current, 1.5);
        // Then create smooth path using Catmull-Rom splines
        pathData = createCatmullRomPath(smoothedPoints, 0.5);
      } else {
        // Use standard smoothing for other tools
        pathData = getPointsToSmoothPath(currentPathRef.current);
      }
    }
    
    // Remove old preview path if exists
    const existingPath = canvas.getObjects().find(
      obj => (obj as any).strokeId === currentStrokeIdRef.current && (obj as any).isPreview === true
    );
    if (existingPath) {
      canvas.remove(existingPath);
    }
    
    // Also remove any orphaned preview paths
    const allPaths = canvas.getObjects().filter(obj => {
      const isPreview = (obj as any).isPreview;
      const strokeId = (obj as any).strokeId;
      
      // Only consider preview paths - must explicitly be marked as preview
      return obj.type === 'path' && 
             isPreview === true &&  // Must be explicitly true, not undefined
             strokeId && 
             strokeId !== currentStrokeIdRef.current;
    });
    
    // Remove old preview paths (older than 5 seconds)
    const currentTime = Date.now();
    allPaths.forEach(path => {
      const strokeId = (path as any).strokeId;
      const timestamp = parseInt(strokeId.split('-')[1] || '0');
      // Only remove if it's definitely a preview and old enough
      if ((path as any).isPreview === true && currentTime - timestamp > 5000) { // 5 seconds old
        canvas.remove(path);
      }
    });

    // Create new path
    const isShape = SHAPE_TOOLS.includes(currentTool as any);
    
    // Special styling for grid and coordinate
    let strokeColor = currentTool === 'eraser' ? '#FFFFFF' : currentColor;
    let strokeOpacity = isShape && isDrawingRef.current ? 0.7 : 1;
    let strokeWidthToUse = currentStrokeWidth;
    
    // Apply highlighter opacity
    if (currentTool === 'highlighter') {
      strokeOpacity = 0.5; // 50% opacity for highlighter
    }
    
    if (currentTool === 'grid') {
      strokeColor = '#999999'; // Gray color for grid
      strokeOpacity = 0.3; // 30% opacity for grid
      strokeWidthToUse = 1; // Thin lines for grid
    } else if (currentTool === 'coordinate') {
      // For coordinate system, we'll use the pressure values to control opacity
      // Grid lines have pressure 0.05, axes have pressure 0.8
      strokeWidthToUse = 2; // Base width for coordinate system
      strokeOpacity = 1; // We'll use pressure to control individual line opacity
    }
    
    // We're now using stroke-based paths, not filled paths
    const path = new Path(pathData, {
      stroke: strokeColor,
      strokeWidth: strokeWidthToUse,
      fill: '',
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      selectable: false,
      evented: false,
      strokeDashArray: isShape && isDrawingRef.current && currentTool !== 'grid' ? [5, 5] : undefined, // Dashed line for shape preview (except grid)
      opacity: strokeOpacity,
      globalCompositeOperation: 'source-over' // Always use normal drawing mode
    });
    
    (path as any).strokeId = currentStrokeIdRef.current;
    // Don't mark preview paths as user drawings - they're temporary
    (path as any).isPreview = true;
    (path as any).questionId = currentQuestionId; // Add question ID
    
    canvas.add(path);
    
    // Use requestAnimationFrame for smoother rendering
    requestAnimationFrame(() => {
      canvas.requestRenderAll();
    });
  }, [fabricCanvasRef, currentTool, currentColor, currentStrokeWidth, generateShapePoints, getShapePathString, currentQuestionId]);

  const updateAreaEraserPreview = useCallback((x: number, y: number) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !areaEraserStartRef.current) return;

    // Remove previous preview
    if (areaEraserPreviewRef.current) {
      canvas.remove(areaEraserPreviewRef.current);
    }

    const startPoint = areaEraserStartRef.current;
    const left = Math.min(startPoint.x, x);
    const top = Math.min(startPoint.y, y);
    const width = Math.abs(x - startPoint.x);
    const height = Math.abs(y - startPoint.y);

    // Create preview rectangle
    const previewRect = new Rect({
      left,
      top,
      width,
      height,
      fill: 'rgba(255, 0, 0, 0.1)', // Light red fill
      stroke: 'red',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      name: 'areaEraserPreview' // Add name for identification
    } as any);

    canvas.add(previewRect);
    areaEraserPreviewRef.current = previewRect;
    canvas.requestRenderAll();
  }, [fabricCanvasRef]);

  // Start drawing
  const startDrawing = useCallback((e: any) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !isDrawingModeEnabled || isPinchingRef.current) return;
    
    // Check palm rejection
    if (!shouldAllowInput(e)) {
      return;
    }

    // Detect input type and track pointer
    let pointerEvent: PointerEvent | null = null;
    if (e && 'pointerId' in e) {
      pointerEvent = e as PointerEvent;
    } else if (e && e.e && 'pointerId' in e.e) {
      pointerEvent = e.e as PointerEvent;
    }
    
    if (pointerEvent) {
      const isPen = detectPenInput(pointerEvent);
      const pointerInfo: PointerInfo = {
        type: pointerEvent.pointerType,
        timestamp: Date.now(),
        isPen,
        position: { x: 0, y: 0 }, // Will be updated with actual position
        isActivelyDrawing: true
      };
      activePointers.current.set(pointerEvent.pointerId, pointerInfo);
      
    }
    
    const isTouch = e.type?.includes('touch') || pointerEvent?.pointerType === 'touch';
    const isPen = pointerEvent ? detectPenInput(pointerEvent) : false;
    const isMouse = !isTouch && !isPen;
    
    lastPointerTypeRef.current = isPen ? 'pen' : isTouch ? 'touch' : 'mouse';

    // For laser pointer, always allow
    if (currentTool === 'laser') {
      setIsDrawing(true);
      currentPathRef.current = [];
      const point = getPointerPosition(e);
      currentPathRef.current.push(point);
      lastPointRef.current = point;
      
      // Generate laser stroke ID
      currentStrokeIdRef.current = generateStrokeId();
      
      // Record laser start event
      if (recordingSession?.isRecording) {
        addRecordingEvent({
          type: 'laser_start',
          timestamp: Date.now(),
          data: {
            point: point
          }
        });
      }
      
      return;
    }

    // For area eraser
    if (currentTool === 'area-eraser') {
      // Use the fabric pointer if available
      if (e.fabricPointer) {
        startAreaEraser(e.fabricPointer.x, e.fabricPointer.y);
      } else {
        const pointer = canvas.getPointer(e.e || e);
        startAreaEraser(pointer.x, pointer.y);
      }
      return;
    }

    // For object eraser
    if (currentTool === 'object-eraser') {
      const point = getPointerPosition(e);
      currentPathRef.current = [point];
      setIsDrawing(true);
      return;
    }

    // Start normal drawing
    setIsDrawing(true);
    drawingStartTimeRef.current = Date.now();
    currentPathRef.current = [];
    
    const point = getPointerPosition(e);
    currentPathRef.current.push(point);
    lastPointRef.current = point;

    // Generate stroke ID
    currentStrokeIdRef.current = generateStrokeId();

    // Add initial stroke
    const strokeData = {
      id: currentStrokeIdRef.current,
      tool: currentTool as Tool,
      color: currentTool === 'eraser' ? '#FFFFFF' : currentColor,
      width: currentStrokeWidth,
      points: [point],
      timestamp: Date.now(),
      questionId: currentQuestionId // Add question ID to stroke
    };

    // Don't add stroke yet - wait until we have more than one point
    // addStroke(strokeData);

    // Record if recording
    if (isRecording) {
      addRecordingEvent({
        type: 'stroke_start',
        timestamp: Date.now(),
        data: {
          tool: currentTool as Tool,
          color: strokeData.color,
          width: currentStrokeWidth,
          point: point,
          strokeId: strokeData.id
        }
      });
    }
  }, [fabricCanvasRef, isDrawingModeEnabled, currentTool, currentColor, 
      currentStrokeWidth, getPointerPosition, addStroke, isRecording, addRecordingEvent, setIsDrawing, currentQuestionId]);

  // Continue drawing
  const continueDrawing = useCallback((e: any) => {
    if (!isDrawingRef.current || isPinchingRef.current) return;
    
    // Check if this pointer is allowed to continue drawing
    let pointerEvent: PointerEvent | null = null;
    if (e && 'pointerId' in e) {
      pointerEvent = e as PointerEvent;
    } else if (e && e.e && 'pointerId' in e.e) {
      pointerEvent = e.e as PointerEvent;
    }
    
    if (pointerEvent) {
      const pointerInfo = activePointers.current.get(pointerEvent.pointerId);
      if (!pointerInfo || !pointerInfo.isActivelyDrawing) {
        return;
      }
      
      // Runtime check: if pen mode is active and this is a touch, stop it
      if (pointerEvent.pointerType === 'touch' && isPenMode && !touchEnabled) {
        pointerInfo.isActivelyDrawing = false;
        return;
      }
      
      // Check if any pen is currently active
      const hasActivePen = Array.from(activePointers.current.values()).some(
        p => p.isPen && p.isActivelyDrawing
      );
      
      if (hasActivePen && pointerEvent.pointerType === 'touch') {
        pointerInfo.isActivelyDrawing = false;
        return;
      }
    }

    // Process coalesced events for smoother pen input (if available)
    let pointsToProcess: Point[] = [];
    
    if (pointerEvent && 'getCoalescedEvents' in pointerEvent && typeof pointerEvent.getCoalescedEvents === 'function') {
      try {
        const coalescedEvents = pointerEvent.getCoalescedEvents();
        if (coalescedEvents && coalescedEvents.length > 0) {
          // Process all coalesced events for smoother drawing
          pointsToProcess = coalescedEvents.map(event => getPointerPosition(event));
        }
      } catch (err) {
        // Fallback if getCoalescedEvents fails
      }
    }
    
    // If no coalesced events, just process the single point
    if (pointsToProcess.length === 0) {
      pointsToProcess = [getPointerPosition(e)];
    }
    
    // For shape tools, only track end point
    if (SHAPE_TOOLS.includes(currentTool as any)) {
      const point = pointsToProcess[pointsToProcess.length - 1]; // Use last point
      // Replace the second point (end point) for shapes
      if (currentPathRef.current.length === 1) {
        currentPathRef.current.push(point);
      } else {
        currentPathRef.current[1] = point;  // Only update end point
      }
      lastPointRef.current = point;
      
      // Update canvas with preview shape
      updateCanvasPath();
      
      // Record shape preview updates
      if (recordingSession?.isRecording && currentStrokeIdRef.current && currentPathRef.current.length === 2) {
        // Generate preview points for the current shape
        const previewPoints = generateShapePoints(currentTool, currentPathRef.current[0], currentPathRef.current[1]);
        
        // Record shape preview event
        addRecordingEvent({
          type: 'shape_preview',
          timestamp: Date.now(),
          data: {
            strokeId: currentStrokeIdRef.current,
            tool: currentTool,
            startPoint: currentPathRef.current[0],
            endPoint: currentPathRef.current[1],
            points: previewPoints
          }
        });
      }
      
      return;
    }
    
    // For laser pointer
    if (currentTool === 'laser') {
      // Add all points for smoother laser trail
      pointsToProcess.forEach(pt => {
        currentPathRef.current.push(pt);
        
        // Record laser move event
        if (recordingSession?.isRecording && currentStrokeIdRef.current) {
          addRecordingEvent({
            type: 'laser_move',
            timestamp: Date.now(),
            data: {
              point: pt
            }
          });
        }
      });
      
      // Draw laser trail
      const canvas = fabricCanvasRef.current;
      if (canvas && currentPathRef.current.length > 1) {
        const path = getPointsToSmoothPath(currentPathRef.current);
        const laserPath = new Path(path, {
          stroke: '#FF0000',
          strokeWidth: 3,
          fill: '',
          opacity: 0.8,
          selectable: false,
          evented: false
        });
        
        canvas.add(laserPath);
        canvas.requestRenderAll();
        
        // Fade out
        setTimeout(() => {
          canvas.remove(laserPath);
          canvas.requestRenderAll();
        }, 1000);
      }
      return;
    }

    // For area eraser - use last point only
    if (currentTool === 'area-eraser' && areaEraserStartRef.current) {
      const point = pointsToProcess[pointsToProcess.length - 1];
      // Use the fabric pointer if available for area eraser
      if (e.fabricPointer) {
        updateAreaEraserPreview(e.fabricPointer.x, e.fabricPointer.y);
      } else {
        updateAreaEraserPreview(point.x, point.y);
      }
      return;
    }

    // For object eraser - process all points for smooth erasing
    if (currentTool === 'object-eraser') {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const allObjectsToRemove = new Set<FabricObject>();
      
      // Process each point
      pointsToProcess.forEach(point => {
        canvas.getObjects().forEach(obj => {
          // Skip protected objects
          const objName = (obj as any).name || '';
          if (objName === 'questionBackground' ||
              objName === 'questionFrame' ||
              objName === 'questionResizeHandle' ||
              objName === 'questionMoveHandle' ||
              objName === 'questionMoveIcon' ||
              objName.startsWith('opticalChoice_')) {
            return;
          }

          // Check if point is inside the object's bounding box
          if (obj.containsPoint(new FabricPoint(point.x, point.y))) {
            allObjectsToRemove.add(obj as FabricObject);
          }
        });
        
        // Track the eraser path for visual feedback
        currentPathRef.current.push(point);
      });

      // Remove all intersecting objects
      allObjectsToRemove.forEach(obj => {
        const strokeId = (obj as any).strokeId;
        if (strokeId) {
          // Remove from canvas
          canvas.remove(obj);
          // Remove from store
          removeStroke(strokeId);
        }
      });

      if (allObjectsToRemove.size > 0) {
        canvas.requestRenderAll();
      }
      
      return;
    }

    // Continue normal drawing
    if (!currentStrokeIdRef.current) return;

    // Add all points to path for smoother drawing
    pointsToProcess.forEach(point => {
      currentPathRef.current.push(point);
      lastPointRef.current = point;
    });
    
    

    // Don't add eraser strokes to the store
    if (currentTool !== 'eraser') {
      // Update stroke with all new points
      const currentStroke = strokes.find(s => s.id === currentStrokeIdRef.current);
      if (currentStroke) {
        // Ensure we're updating with all points including new ones
        const updatedPoints = [...currentPathRef.current];
        
        // Only update if we have more than one point (to avoid single-point strokes)
        if (updatedPoints.length > 1) {
          updateStroke(currentStrokeIdRef.current, {
            ...currentStroke,
            points: updatedPoints
          });
        }
      } else if (currentPathRef.current.length > 1) {
        // Stroke not yet added to store, add it now that we have more than one point
        const strokeData = {
          id: currentStrokeIdRef.current,
          tool: currentTool as Tool,
          color: currentColor,
          width: currentStrokeWidth,
          points: [...currentPathRef.current],
          timestamp: Date.now(),
          questionId: currentQuestionId
        };
        addStroke(strokeData);
      }
    }

    // Update canvas
    updateCanvasPath();

    // Record if recording - record all points
    if (isRecording && currentStrokeIdRef.current) {
      // Capture strokeId value to avoid closure issues
      const strokeId = currentStrokeIdRef.current;
      pointsToProcess.forEach(point => {
        addRecordingEvent({
          type: 'stroke_add',
          timestamp: Date.now(),
          data: {
            strokeId: strokeId,
            point: point
          }
        });
      });
    }
  }, [currentTool, getPointerPosition, fabricCanvasRef, currentStrokeWidth,
      currentColor, updateStroke, isRecording, addRecordingEvent, updateCanvasPath, updateAreaEraserPreview, removeStroke, isPenMode, touchEnabled, strokes, currentQuestionId]);

  // Start area eraser selection
  const startAreaEraser = useCallback((x: number, y: number) => {
    isDrawingRef.current = true;
    setIsDrawing(true);
    areaEraserStartRef.current = { x, y, pressure: 0.5, timestamp: Date.now() };
    
    // Remove any existing preview
    if (areaEraserPreviewRef.current && fabricCanvasRef.current) {
      fabricCanvasRef.current.remove(areaEraserPreviewRef.current);
      areaEraserPreviewRef.current = null;
    }
    
    // Record area erase start
    const { addRecordingEvent } = useWhiteboardStore.getState();
    addRecordingEvent({
      type: 'area_erase_start',
      timestamp: Date.now(),
      data: { startPoint: { x, y } }
    });
  }, [setIsDrawing]);

  // Finish area eraser and delete objects in selection
  const finishAreaEraser = useCallback((x: number, y: number) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !areaEraserStartRef.current) return;

    isDrawingRef.current = false;
    setIsDrawing(false);

    const startPoint = areaEraserStartRef.current;
    const selectionRect = {
      left: Math.min(startPoint.x, x),
      top: Math.min(startPoint.y, y),
      right: Math.max(startPoint.x, x),
      bottom: Math.max(startPoint.y, y)
    };

    // Remove preview rectangle
    if (areaEraserPreviewRef.current) {
      canvas.remove(areaEraserPreviewRef.current);
      areaEraserPreviewRef.current = null;
    }

    // Find objects that intersect with selection rectangle
    const objectsToRemove: FabricObject[] = [];
    const strokeIdsToRemove: string[] = [];

    canvas.getObjects().forEach(obj => {
      // Skip protected question elements
      const objName = getObjectName(obj);
      if (objName === 'questionBackground' || 
          objName === 'parentQuestionBackground' ||
          objName === 'questionFrame' ||
          objName === 'questionResizeHandle' ||
          objName === 'questionMoveHandle' ||
          objName === 'questionMoveIcon' ||
          objName?.startsWith('opticalChoice_')) {
        return;
      }
      
      const objBounds = obj.getBoundingRect();
      
      // Check if object intersects with selection rectangle
      const intersects = !(
        objBounds.left + objBounds.width < selectionRect.left ||
        objBounds.left > selectionRect.right ||
        objBounds.top + objBounds.height < selectionRect.top ||
        objBounds.top > selectionRect.bottom
      );

      if (intersects) {
        const strokeId = obj.get('strokeId') as string;
        if (strokeId) {
          objectsToRemove.push(obj as FabricObject);
          strokeIdsToRemove.push(strokeId);
        }
      }
    });

    // Remove objects from fabric canvas
    objectsToRemove.forEach(obj => canvas.remove(obj));

    // Remove strokes from store
    if (strokeIdsToRemove.length > 0) {
      const { strokes } = useWhiteboardStore.getState();
      const updatedStrokes = strokes.filter(stroke => !strokeIdsToRemove.includes(stroke.id));
      useWhiteboardStore.setState({ strokes: updatedStrokes });
      
      // Save state after area erasure
      const { saveState } = useWhiteboardStore.getState();
      saveState();
    }

    // Reset area eraser state
    areaEraserStartRef.current = null;
    canvas.requestRenderAll();

    // Record area erase event
    addRecordingEvent({
      type: 'area_erase_end',
      timestamp: 0,
      data: { 
        endPoint: { x, y },
        deletedObjectIds: strokeIdsToRemove
      }
    });
  }, [setIsDrawing, addRecordingEvent]);

  const finalizeCurrentStroke = useCallback(() => {
    if (!currentStrokeIdRef.current) return;

    // Any final processing for the stroke
    const finalStrokeData = {
      id: currentStrokeIdRef.current,
      tool: currentTool,
      color: currentTool === 'eraser' ? '#FFFFFF' : currentColor,
      width: currentStrokeWidth,
      points: currentPathRef.current,
      timestamp: Date.now(),
      duration: Date.now() - drawingStartTimeRef.current
    };

    // Could emit events or do other finalization here
  }, [currentTool, currentColor, currentStrokeWidth]);

  // End drawing
  const endDrawing = useCallback((e: any) => {
    // Always check and update both ref and state
    if (!isDrawingRef.current && !isDrawing) return;
    
    // Debug log for troubleshooting
    if (DEBUG_PALM_REJECTION) {
      console.log('endDrawing called - current isDrawing state:', isDrawingRef.current);
    }
    
    // Clean up pointer tracking
    let pointerEvent: PointerEvent | null = null;
    if (e && 'pointerId' in e) {
      pointerEvent = e as PointerEvent;
    } else if (e && e.e && 'pointerId' in e.e) {
      pointerEvent = e.e as PointerEvent;
    }
    
    if (pointerEvent) {
      const pointerInfo = activePointers.current.get(pointerEvent.pointerId);
      if (pointerInfo) {
        pointerInfo.isActivelyDrawing = false;
      }
      activePointers.current.delete(pointerEvent.pointerId);
      rejectedTouches.current.delete(pointerEvent.pointerId);
      
    }

    // Always ensure drawing state is reset
    setIsDrawing(false);
    isDrawingRef.current = false;

    // For laser pointer
    if (currentTool === 'laser') {
      // Record laser end event
      if (recordingSession?.isRecording && currentStrokeIdRef.current) {
        addRecordingEvent({
          type: 'laser_end',
          timestamp: Date.now(),
          data: {}
        });
      }
      
      currentPathRef.current = [];
      currentStrokeIdRef.current = null;
      return;
    }

    // For area eraser
    if (currentTool === 'area-eraser') {
      if (areaEraserStartRef.current) {
        // Use the fabric pointer if available
        if (e.fabricPointer) {
          finishAreaEraser(e.fabricPointer.x, e.fabricPointer.y);
        } else {
          const canvas = fabricCanvasRef.current;
          if (canvas) {
            const pointer = canvas.getPointer(e.e || e);
            finishAreaEraser(pointer.x, pointer.y);
          }
        }
      }
      return;
    }

    // For object eraser
    if (currentTool === 'object-eraser') {
      currentPathRef.current = [];
      return;
    }

    // Complete normal drawing
    if (currentStrokeIdRef.current) {
      // Check if we have more than one point
      
      if (currentPathRef.current.length === 0) {
        // Remove empty stroke (no points at all)
        removeStroke(currentStrokeIdRef.current);
        
        // Reset state
        currentPathRef.current = [];
        currentStrokeIdRef.current = null;
        lastPointRef.current = null;
        return;
      }
      
      // For single-point strokes (dots), ensure they're visible
      if (currentPathRef.current.length === 1) {
        // Continue to render the dot - don't return early
      }
      
      const drawingDuration = Date.now() - drawingStartTimeRef.current;
      
      // Shape recognition removed - not needed

      // Create final path if not replaced by shape
      const canvas = fabricCanvasRef.current;
      if (canvas) {
        // Creating final path

        // Remove the preview path
        const existingPath = canvas.getObjects().find(
          obj => (obj as any).strokeId === currentStrokeIdRef.current && (obj as any).isPreview === true
        );
        if (existingPath) {
          // Remove preview path
          canvas.remove(existingPath);
        }
        
        // Clean up only preview paths (not user-drawn shapes)
        const tempPaths = canvas.getObjects().filter(obj => {
          if (obj.type !== 'path') return false;
          const isPreview = (obj as any).isPreview;
          const isUserDrawing = (obj as any).isUserDrawing;
          // Only remove paths explicitly marked as preview and NOT user drawings
          return isPreview === true && isUserDrawing !== true;
        });
        
        
        tempPaths.forEach(path => canvas.remove(path));

        // Create final path
        let pathData: string;
        let finalPoints: Point[];
        
        if (SHAPE_TOOLS.includes(currentTool as any) && currentPathRef.current.length === 2) {
          // For shapes, generate final shape points and use straight lines
          const [startPoint, endPoint] = currentPathRef.current;
          finalPoints = generateShapePoints(currentTool, startPoint, endPoint);
          pathData = getShapePathString(finalPoints, currentTool);
          
          // Add stroke_add events for shape points
          if (recordingSession?.isRecording && currentStrokeIdRef.current) {
            // Capture strokeId value to avoid closure issues
            const strokeId = currentStrokeIdRef.current;
            
            // Add all shape points as stroke_add events
            finalPoints.forEach((point, index) => {
              // Skip the first point as it's already in stroke_start
              if (index > 0) {
                addRecordingEvent({
                  type: 'stroke_add',
                  timestamp: Date.now(),
                  data: {
                    strokeId: strokeId,
                    point: point
                  }
                });
              }
            });
          }
        } else {
          // For freehand drawing, use advanced smoothing
          finalPoints = currentPathRef.current;
          
          if (currentTool === 'pen' || currentTool === 'pencil') {
            // Apply gaussian smoothing first
            const smoothedPoints = gaussianSmooth(finalPoints, 1.5);
            // Then create smooth path using Catmull-Rom splines
            pathData = createCatmullRomPath(smoothedPoints, 0.5);
          } else {
            // Use standard smoothing for other tools
            pathData = getPointsToSmoothPath(finalPoints);
          }
        }
        
        // Define stroke properties outside of conditional blocks
        let strokeColor = currentColor;
        let finalOpacity = 1;
        let strokeWidthToUse = currentStrokeWidth;
        
        // Handle eraser tool - white mask approach
        if (currentTool === 'eraser') {
          strokeColor = '#FFFFFF';
          // Check if eraser path intersects with any pen/highlighter strokes
          const eraserBounds = {
            left: Math.min(...currentPathRef.current.map(p => p.x)) - currentStrokeWidth,
            right: Math.max(...currentPathRef.current.map(p => p.x)) + currentStrokeWidth,
            top: Math.min(...currentPathRef.current.map(p => p.y)) - currentStrokeWidth,
            bottom: Math.max(...currentPathRef.current.map(p => p.y)) + currentStrokeWidth
          };
          
          let intersectsWithPenOrHighlighter = false;
          canvas.getObjects().forEach(obj => {
            const tool = (obj as any).tool;
            const isUserDrawing = (obj as any).isUserDrawing;
            const isPreview = (obj as any).isPreview;
            
            if (isUserDrawing && !isPreview && (tool === 'pen' || tool === 'highlighter')) {
              const objBounds = obj.getBoundingRect();
              
              // Check bounds intersection
              if (eraserBounds.left <= objBounds.left + objBounds.width &&
                  eraserBounds.right >= objBounds.left &&
                  eraserBounds.top <= objBounds.top + objBounds.height &&
                  eraserBounds.bottom >= objBounds.top) {
                intersectsWithPenOrHighlighter = true;
              }
            }
          });
          
          // Only create white mask if it intersects with pen/highlighter
          if (intersectsWithPenOrHighlighter) {
            const eraserPath = new Path(pathData, {
              stroke: strokeColor,
              strokeWidth: strokeWidthToUse,
              fill: '',
              strokeLineCap: 'round',
              strokeLineJoin: 'round',
              selectable: false,
              evented: false,
              strokeDashArray: undefined,
              opacity: 1,
              globalCompositeOperation: 'source-over' // Normal drawing mode
            });
            
            (eraserPath as any).strokeId = currentStrokeIdRef.current;
            (eraserPath as any).id = currentStrokeIdRef.current;
            (eraserPath as any).isUserDrawing = true;
            (eraserPath as any).isPreview = false;
            (eraserPath as any).questionId = currentQuestionId;
            (eraserPath as any).tool = 'eraser';
            (eraserPath as any).isWhiteMask = true; // Mark as white mask
            
            canvas.add(eraserPath);
          }
        } else {
          // Normal drawing tools
          
          // Apply highlighter opacity
          if (currentTool === 'highlighter') {
            finalOpacity = 0.5; // 50% opacity for highlighter
          }
          
          if (currentTool === 'grid') {
            strokeColor = '#999999'; // Gray color for grid
            finalOpacity = 0.3; // 30% opacity for grid
            strokeWidthToUse = 1; // Thin lines for grid
          } else if (currentTool === 'coordinate') {
            strokeWidthToUse = 2; // Thicker lines for axes
          }
          
          // We're now using stroke-based paths with advanced smoothing
          const finalPath = new Path(pathData, {
            stroke: strokeColor,
            strokeWidth: strokeWidthToUse,
            fill: '',
            strokeLineCap: 'round',
            strokeLineJoin: 'round',
            selectable: false,
            evented: false,
            strokeDashArray: undefined, // No dashed line for final shape
            opacity: finalOpacity,
            globalCompositeOperation: 'source-over'
          });
          
          (finalPath as any).strokeId = currentStrokeIdRef.current;
          (finalPath as any).id = currentStrokeIdRef.current; // Add id for tracking
          (finalPath as any).isUserDrawing = true; // Mark as user drawing to prevent removal on question change
          (finalPath as any).isPreview = false; // Explicitly mark as NOT a preview
          (finalPath as any).questionId = currentQuestionId; // Add question ID for filtering
          (finalPath as any).tool = currentTool; // Add tool type for tracking
          
          // Debug log for shapes
          if (SHAPE_TOOLS.includes(currentTool as any)) {
          }
          
          canvas.add(finalPath);
          
          // Check if stroke is on an optical choice (only for non-eraser tools)
          const questionIdFromStore = useWhiteboardStore.getState().currentQuestionId;
          const effectiveQuestionId = currentQuestionId || questionIdFromStore;
          if (effectiveQuestionId && isOpticalMode) {
            const strokeBounds = finalPath.getBoundingRect();
            const opticalChoice = isStrokeOnOpticalChoice(canvas, strokeBounds, effectiveQuestionId);
            if (opticalChoice) {
              // Associate this stroke with the optical choice
              associateStrokeWithChoice(
                opticalChoice.questionId,
                opticalChoice.choiceLabel,
                currentStrokeIdRef.current
              );
            }
          }
        }
        
        // For shapes, we need to add the stroke to the store if it doesn't exist yet
        if (SHAPE_TOOLS.includes(currentTool as any) && currentStrokeIdRef.current) {
          const existingStroke = strokes.find(s => s.id === currentStrokeIdRef.current);
          if (existingStroke) {
            updateStroke(currentStrokeIdRef.current, {
              ...existingStroke,
              points: finalPoints
            });
          } else {
            // Stroke doesn't exist yet - add it now with final points
            const strokeData = {
              id: currentStrokeIdRef.current,
              tool: currentTool as Tool,
              color: strokeColor,
              width: strokeWidthToUse,
              points: finalPoints,
              timestamp: Date.now(),
              questionId: currentQuestionId
            };
            addStroke(strokeData);
          }
          
          // Verify the stroke is in store
        }
        
        // DEBUG: Canvas durumunu kontrol et
        // Debug logs only in optical mode
        if (isOpticalMode) {
          const debugOpticals = canvas.getObjects().filter(obj => {
            const name = obj.get?.('name') || (obj as any).name;
            return name?.includes('optical');
          });
        }
        
        canvas.requestRenderAll();
        
        // Record the complete stroke for playback
        if (isRecording && currentStrokeIdRef.current) {
          const strokeData = {
            id: currentStrokeIdRef.current,
            tool: currentTool as Tool,
            color: strokeColor,
            width: strokeWidthToUse,
            points: finalPoints,
            timestamp: Date.now(),
            questionId: currentQuestionId
          };
          
     
          
          addRecordingEvent({
            type: 'add_stroke',
            timestamp: Date.now(),
            data: {
              stroke: strokeData
            }
          });
        }
        
        // Optical choice detection - ONLY in optical mode
        if (isOpticalMode) {
         
          
          if (onOpticalChoiceCircled && onOpticalChoiceEliminated) {
            try {
              const strokePath = [...currentPathRef.current];
              
              if (currentTool === 'pen' && strokePath.length > 5) {
              
              
              // Log stroke bounds for debugging
              let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
              strokePath.forEach(point => {
                minX = Math.min(minX, point.x);
                maxX = Math.max(maxX, point.x);
                minY = Math.min(minY, point.y);
                maxY = Math.max(maxY, point.y);
              });
             
              
            
             
            
            // Canvas'taki tüm optik seçimleri bul
            const opticalChoices = canvas.getObjects().filter(obj => {
              const name = obj.get?.('name') || (obj as any).name;
              const isOptical = obj.type === 'group' && name?.startsWith('questionOpticalChoice_');
              if (isOptical) {
                const bounds = obj.getBoundingRect();
              }
              return isOptical;
            });
            
         
           
        
       
      
        
        // Get optical choices from canvas
        const opticalChoiceObjects = canvas.getObjects().filter(obj => {
          const name = obj.get?.('name') || (obj as any).name || getObjectName(obj);
          return obj.type === 'group' && name?.startsWith('questionOpticalChoice_');
        });
        
       
          
          if (opticalChoiceObjects.length > 0) {
            // Format optical choices for detection
            const formattedChoices = opticalChoiceObjects.map((obj, index) => {
            const bounds = obj.getBoundingRect();
            const name = obj.get?.('name') || (obj as any).name || getObjectName(obj);
            let choiceLabel = (obj as any).choiceLabel;
            
            if (!choiceLabel && name) {
              // Extract choice label from name like "questionOpticalChoice_123_A"
              const parts = name.split('_');
              if (parts.length >= 3) {
                choiceLabel = parts[2]; // Get the letter part
              } else {
                choiceLabel = parts[parts.length - 1];
              }
            }
            if (!choiceLabel) {
              choiceLabel = String.fromCharCode(65 + index);
            }
            
           
            
            return {
              id: choiceLabel,
              x: bounds.left,
              y: bounds.top,
              width: bounds.width,
              height: bounds.height,
              label: choiceLabel
            };
          });
          
          // FIRST check for elimination (line through choice)
       
          const eliminatedChoice = findEliminatedOpticalChoice(strokePath, formattedChoices);
        
          
          if (eliminatedChoice && onOpticalChoiceEliminated) {
            // eliminatedChoice is now the choice label (A, B, C, etc.)
            const choiceIndex = eliminatedChoice.charCodeAt(0) - 65; // Convert A->0, B->1, etc.
          
            onOpticalChoiceEliminated(choiceIndex);
          } else {
            // ONLY check for selection if NO elimination was detected
         
            // Check if any optical choice was circled (NOT with straight lines)
            const circledChoice = findCircledOpticalChoice(strokePath, formattedChoices);
          
            
            if (circledChoice && onOpticalChoiceCircled) {
              const choiceIndex = circledChoice.charCodeAt(0) - 65;
            
              onOpticalChoiceCircled(choiceIndex);
              
              // Visual feedback - animate and remove the stroke
              const strokeObject = canvas.getObjects().find(
                obj => (obj as any).strokeId === currentStrokeIdRef.current
              );
              if (strokeObject) {
                // Animate to green
                strokeObject.animate({
                  stroke: '#10b981'
                }, {
                  duration: 300,
                  onChange: () => canvas.requestRenderAll(),
                  onComplete: () => {
                    // Remove the stroke after animation
                    setTimeout(() => {
                      canvas.remove(strokeObject);
                      // Also remove from store
                      removeStroke(currentStrokeIdRef.current!);
                      canvas.requestRenderAll();
                    }, 200);
                  }
                });
              }
            }
              }
            } // End of if (opticalChoiceObjects.length > 0)
          }
        } catch (error) {
        }
      } else {
      }
    } // End of optical mode check
      } // End of if (canvas)
    } // End of if (currentStrokeIdRef.current)

    // Record stroke_end event for playback animation
    if (isRecording && currentStrokeIdRef.current && currentTool !== 'laser' && currentTool !== 'area-eraser' && currentTool !== 'object-eraser') {
      // Capture strokeId value to avoid closure issues
      const strokeId = currentStrokeIdRef.current;
      addRecordingEvent({
        type: 'stroke_end',
        timestamp: Date.now(),
        data: {
          strokeId: strokeId,
          // Include shape points for shape tools if available
          ...(SHAPE_TOOLS.includes(currentTool as any) && currentPathRef.current.length > 0 ? {
            shapePoints: currentPathRef.current
          } : {})
        }
      });
    }

    // Reset
    currentPathRef.current = [];
    currentStrokeIdRef.current = null;
    lastPointRef.current = null;
  }, [currentTool, finishAreaEraser, finalizeCurrentStroke, 
      fabricCanvasRef, currentColor, currentStrokeWidth, generateShapePoints, getShapePathString,
      isOpticalMode, onOpticalChoiceCircled, onOpticalChoiceEliminated, setIsDrawing, removeStroke, currentQuestionId,
      strokes, updateStroke, addStroke, isRecording, addRecordingEvent]);
  
  // Clean up area eraser preview when tool changes
  useEffect(() => {
    // If we're switching away from area-eraser tool, clean up any preview
    if (currentTool !== 'area-eraser' && areaEraserPreviewRef.current && fabricCanvasRef.current) {
      fabricCanvasRef.current.remove(areaEraserPreviewRef.current);
      areaEraserPreviewRef.current = null;
      areaEraserStartRef.current = null;
    }
  }, [currentTool, fabricCanvasRef]);

  // Clean up rejected touches periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      // Clean up old rejected touches (older than 2 seconds)
      const now = Date.now();
      const toDelete: number[] = [];
      
      rejectedTouches.current.forEach((pointerId) => {
        const pointerInfo = activePointers.current.get(pointerId);
        if (!pointerInfo || (now - pointerInfo.timestamp) > 2000) {
          toDelete.push(pointerId);
        }
      });
      
      toDelete.forEach(id => {
        rejectedTouches.current.delete(id);
        activePointers.current.delete(id);
      });
    }, 1000);
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Global mouse/pointer up handler to catch events outside canvas
  useEffect(() => {
    const handleGlobalMouseUp = (e: MouseEvent | PointerEvent) => {
      // If we're drawing and mouse is released anywhere, end drawing
      if (isDrawingRef.current) {
        if (DEBUG_PALM_REJECTION) {
          console.log('Global mouse up detected while drawing - ending drawing');
        }
        endDrawing(e);
      }
    };

    // Add global event listeners to catch mouse up events outside the canvas
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('pointerup', handleGlobalMouseUp);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('pointerup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('pointerup', handleGlobalMouseUp);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('pointerup', handleGlobalMouseUp);
    };
  }, [endDrawing]);

  // Touch event handlers for pinch zoom
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2) {
      isPinchingRef.current = true;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      initialPinchDistance.current = distance;
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (isPinchingRef.current && e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      const scale = distance / initialPinchDistance.current;
      // Handle pinch zoom here
    }
  }, []);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (e.touches.length < 2) {
      isPinchingRef.current = false;
    }
  }, []);

  return {
    // Event handlers
    startDrawing,
    continueDrawing,
    endDrawing,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    
    // State
    isDrawing,
    isPinching: isPinchingRef.current,
    currentPath: currentPathRef.current,
    lastPointerType: lastPointerTypeRef.current,
    
    // Utilities
    getPointerPosition,
    updateCanvasPath,
    shouldAllowInput,
    
    // Canvas hover state
    isHoveringCanvas,
    setIsHoveringCanvas
  };
};